import { LoadingButton } from "@mui/lab"
import { Button, Dialog, DialogContent, styled, Typography } from "@mui/material"
import { AuthDialog } from "app-constants/constants"
import DialogHeader from "components/dialog/DialogHeader"
import useAuth from "hooks/useAuth"
import useAuthDialog from "hooks/useAuthDialog"
import React from "react"
import { pxToRem } from "utils/helpers"

interface MagicLinkSentDialogProps {}

const MagicLinkSentDialog = (props: MagicLinkSentDialogProps) => {
  const {} = props
  const authDialogs = useAuthDialog()
  const auth = useAuth()
  const [resending, setResending] = React.useState(false)

  const email = authDialogs.payload?.email
  const returnUrl = authDialogs.payload?.returnUrl
  const hasPassword = authDialogs.payload?.hasPassword
  const conventionIdToRegister = authDialogs.payload?.conventionIdToRegister
  const redirectUrl = authDialogs.payload?.redirectUrl

  const handleResend = async () => {
    setResending(true)
    try {
      await auth.sendMagicLink(
        { email, returnUrl },
        { customSuccessMessage: `We sent another link to ${email}` },
      )
    } finally {
      setResending(false)
    }
  }

  const handleUsePassword = () => {
    authDialogs.set(AuthDialog.SIGN_IN, {
      email,
      conventionIdToRegister,
      redirectUrl,
    })
  }

  const handleClose = () => authDialogs.reset()

  return (
    <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogHeader title="Check your email" onClose={handleClose} />
      <_DialogContent>
        <Typography color="gray.darker" component="p">
          We sent a sign-in link to <strong>{email}</strong>. Open the email and click the link —
          that&apos;s it, no password needed.
        </Typography>
        <Typography color="gray.darker" component="p">
          Don&apos;t see it? Check your spam or junk folder, or resend it below.
        </Typography>
        <LoadingButton
          loading={resending}
          size="large"
          color="primary"
          variant="contained"
          onClick={handleResend}>
          Resend link
        </LoadingButton>
        {hasPassword && (
          <_FallbackTextContainer>
            <span>Prefer your password?</span>
            <_FallbackButton size="small" onClick={handleUsePassword}>
              Sign in with password
            </_FallbackButton>
          </_FallbackTextContainer>
        )}
      </_DialogContent>
    </Dialog>
  )
}

export default MagicLinkSentDialog

const _DialogContent = styled(DialogContent)(() => ({
  display: "flex",
  flexFlow: "column nowrap",
  gap: pxToRem(20),
})) as typeof DialogContent

const _FallbackTextContainer = styled(Typography)(({ theme }) => ({
  display: "flex",
  flexFlow: "row wrap",
  alignItems: "center",
  color: theme.palette.gray.darker,
  ...theme.typography.paragraphRegular,
})) as typeof Typography

const _FallbackButton = styled(Button)(({ theme }) => ({
  ...theme.typography.paragraphRegular,
  textTransform: "none",
}))
